import React, { useEffect, useState, useRef } from 'react';
import { Header } from './Header';
import { ContactCard } from './ContactCard';
import { InlineDetailPanel } from './InlineDetailPanel';
import { mockLeads, mockLoans, mockRetention } from '../data/mockData';
import { AnyContact, PowerDialState, SortOption, Lead, Loan, Retention } from '../types';
import { PhoneIcon, PauseIcon, PlayIcon, PhoneOffIcon, ArrowUpDownIcon, ChevronDownIcon } from 'lucide-react';
export function CommandCenter() {
  // Data State
  const [leads, setLeads] = useState(mockLeads);
  const [loans, setLoans] = useState(mockLoans);
  const [retention, setRetention] = useState(mockRetention);
  // UI State
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('followUp');
  const [selectedContact, setSelectedContact] = useState<AnyContact | null>(null);
  const [powerDial, setPowerDial] = useState<PowerDialState>({
    active: false,
    currentLane: null,
    currentIndex: 0,
    isPaused: false,
    callStartTime: null
  });
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Timer for active call
  const [callSeconds, setCallSeconds] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (powerDial.active && !powerDial.isPaused) {
      interval = setInterval(() => setCallSeconds(s => s + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [powerDial.active, powerDial.isPaused]);
  useEffect(() => {
    setCallSeconds(0);
  }, [selectedContact?.id]);
  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };
  // Helpers
  const allContacts = [...leads, ...loans, ...retention];
  const sortContacts = (contacts: AnyContact[], sort: SortOption): AnyContact[] => {
    const today = new Date().toISOString().split('T')[0];
    return [...contacts].sort((a, b) => {
      if (sort === 'followUp') {
        const aIsToday = a.scheduledFollowUp === today;
        const bIsToday = b.scheduledFollowUp === today;
        if (aIsToday && !bIsToday) return -1;
        if (!aIsToday && bIsToday) return 1;
        if (a.scheduledFollowUp && b.scheduledFollowUp) {
          return new Date(a.scheduledFollowUp).getTime() - new Date(b.scheduledFollowUp).getTime();
        }
        if (a.scheduledFollowUp) return -1;
        if (b.scheduledFollowUp) return 1;
        return 0;
      }
      if (sort === 'overdue') {
        const aDate = a.history[0]?.date ? new Date(a.history[0].date).getTime() : 0;
        const bDate = b.history[0]?.date ? new Date(b.history[0].date).getTime() : 0;
        return aDate - bDate;
      }
      if (sort === 'name') {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  };
  const activeData = sortContacts(allContacts, sortBy);
  const todayFollowUpsCount = allContacts.filter(c => c.scheduledFollowUp === new Date().toISOString().split('T')[0]).length;
  // Actions
  const handleStartPowerDial = () => {
    if (activeData.length === 0) return;
    setPowerDial({
      active: true,
      currentLane: 'all',
      currentIndex: 0,
      isPaused: false,
      callStartTime: new Date()
    });
    setSelectedContact(activeData[0]);
  };
  const handleEndPowerDial = () => {
    setPowerDial({
      active: false,
      currentLane: null,
      currentIndex: 0,
      isPaused: false,
      callStartTime: null
    });
    setSelectedContact(null);
  };
  const handleNextCall = () => {
    const nextIndex = powerDial.currentIndex + 1;
    if (nextIndex >= activeData.length) {
      handleEndPowerDial();
    } else {
      setPowerDial(prev => ({
        ...prev,
        currentIndex: nextIndex,
        isPaused: false,
        callStartTime: new Date()
      }));
      setSelectedContact(activeData[nextIndex]);
    }
  };
  const handleTogglePause = () => {
    setPowerDial(prev => ({
      ...prev,
      isPaused: !prev.isPaused
    }));
  };
  const handleManualCall = (contact: AnyContact) => {
    if (powerDial.active) handleEndPowerDial();
    setSelectedContact(contact);
  };
  const handleContactClick = (contact: AnyContact) => {
    if (!powerDial.active) {
      setSelectedContact(contact);
    }
  };
  const handleAddNote = (contactId: string, noteText: string) => {
    const newNote = {
      id: `note-${Date.now()}`,
      date: new Date().toISOString(),
      type: 'note' as const,
      description: noteText
    };
    const addNoteToContact = (prev: any[]) => prev.map((c: AnyContact) => c.id === contactId ? {
      ...c,
      history: [newNote, ...c.history]
    } : c);
    setLeads(addNoteToContact);
    setLoans(addNoteToContact);
    setRetention(addNoteToContact);
    if (selectedContact?.id === contactId) {
      setSelectedContact({
        ...selectedContact,
        history: [newNote, ...selectedContact.history]
      });
    }
  };
  const sortOptions: {
    value: SortOption;
    label: string;
  }[] = [{
    value: 'followUp',
    label: 'Follow-Up Date'
  }, {
    value: 'overdue',
    label: 'Most Overdue'
  }, {
    value: 'name',
    label: 'Name (A-Z)'
  }];
  const currentSortLabel = sortOptions.find(o => o.value === sortBy)?.label || 'Sort';
  return <div className={`flex flex-col h-screen w-full overflow-hidden transition-colors duration-300 ${isDarkMode ? '' : 'light-mode'}`} style={{
    backgroundColor: 'var(--color-bg-app)'
  }} data-id="element-1">
      <Header isDarkMode={isDarkMode} onToggleDarkMode={() => setIsDarkMode(!isDarkMode)} totalContacts={allContacts.length} data-id="element-2" />

      <div className="flex-1 overflow-y-auto custom-scrollbar relative" data-id="element-3">
        <div className="max-w-[720px] mx-auto w-full py-8 px-6 flex flex-col min-h-full" data-id="element-4">
          {/* Top Section: Launch Card OR Active Call Bar */}
          {!powerDial.active ? <div className="bg-olympian-surface border border-olympian-gold/30 rounded-2xl p-8 mb-8 text-center shadow-[0_8px_32px_rgba(212,168,67,0.1)] relative overflow-hidden" data-id="element-5">
              <div className="absolute inset-0 bg-gradient-to-b from-olympian-gold/5 to-transparent pointer-events-none" data-id="element-6" />
              <h2 className="text-2xl font-bold text-theme mb-2 relative z-10" data-id="element-7">
                Ready to dial?
              </h2>
              <p className="text-olympian-textMuted mb-8 relative z-10" data-id="element-8">
                You have{' '}
                <strong className="text-cyan-400" data-id="element-9">
                  {todayFollowUpsCount} follow-ups today
                </strong>
                , {allContacts.length} total in your pipeline.
              </p>
              <button onClick={handleStartPowerDial} disabled={activeData.length === 0} className="relative z-10 mx-auto flex items-center justify-center gap-3 bg-olympian-gold text-olympian-navy px-10 py-4 rounded-xl text-lg font-bold hover:bg-olympian-goldHover hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_0_24px_rgba(212,168,67,0.4)] disabled:opacity-50 disabled:pointer-events-none" data-id="element-10">
                <PhoneIcon className="w-6 h-6" data-id="element-11" />
                Start Power Dialing
              </button>
            </div> : <div className="sticky top-0 z-30 bg-olympian-surface/95 backdrop-blur-md border border-olympian-border rounded-xl p-4 mb-6 shadow-xl flex items-center justify-between animate-crossfade" data-id="element-12">
              <div className="flex items-center gap-4" data-id="element-13">
                <div className="w-10 h-10 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center text-green-400 relative" data-id="element-14">
                  {!powerDial.isPaused && <div className="absolute inset-0 rounded-full border border-green-400 animate-pulse-slow opacity-40" data-id="element-15" />}
                  <PhoneIcon className="w-4 h-4" data-id="element-16" />
                </div>
                <div data-id="element-17">
                  <div className="text-sm font-bold text-theme flex items-center gap-2" data-id="element-18">
                    Calling {selectedContact?.name}
                    <span className="text-olympian-textMuted font-normal" data-id="element-19">
                      ·
                    </span>
                    <span className={`font-mono ${powerDial.isPaused ? 'text-olympian-textMuted' : 'text-theme'}`} data-id="element-20">
                      {formatTime(callSeconds)}
                    </span>
                  </div>
                  <div className="text-xs text-olympian-textMuted font-mono mt-0.5" data-id="element-21">
                    Call {powerDial.currentIndex + 1} of {activeData.length}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-22">
                <button onClick={handleTogglePause} className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all border ${powerDial.isPaused ? 'bg-olympian-gold text-olympian-navy hover:bg-olympian-goldHover border-transparent shadow-[0_0_12px_rgba(212,168,67,0.25)]' : 'bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20'}`} data-id="element-23">
                  {powerDial.isPaused ? <PlayIcon className="w-4 h-4" data-id="element-24" /> : <PauseIcon className="w-4 h-4" data-id="element-25" />}
                  {powerDial.isPaused ? 'Resume' : 'Pause'}
                </button>
                <button onClick={handleEndPowerDial} className="px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20" data-id="element-26">
                  <PhoneOffIcon className="w-4 h-4" data-id="element-27" />
                  End Session
                </button>
              </div>
            </div>}

          {/* Active Call Detail Panel (Only shows during power dial or manual selection) */}
          {selectedContact && <div className="mb-8 border border-olympian-border rounded-xl overflow-hidden shadow-2xl h-[500px] animate-crossfade shrink-0" data-id="element-28">
              <InlineDetailPanel contact={selectedContact} onClose={() => !powerDial.active && setSelectedContact(null)} onAddNote={handleAddNote} isPowerDialing={powerDial.active} data-id="element-29" />
            </div>}

          {/* List Header & Sort */}
          <div className="flex items-center justify-between mb-4 mt-2" data-id="element-30">
            <h3 className="text-sm font-bold text-olympian-textMuted uppercase tracking-wider" data-id="element-31">
              Up Next
            </h3>
            <div className="relative" ref={sortRef} data-id="element-32">
              <button onClick={() => !powerDial.active && setSortOpen(!sortOpen)} disabled={powerDial.active} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border border-olympian-border bg-olympian-surface/50 transition-colors text-sm ${powerDial.active ? 'opacity-50 cursor-not-allowed text-olympian-textMuted' : 'hover:bg-olympian-surface text-theme'}`} data-id="element-33">
                <ArrowUpDownIcon className="w-3.5 h-3.5 text-olympian-textMuted" data-id="element-34" />
                <span className="font-medium" data-id="element-35">{currentSortLabel}</span>
                <ChevronDownIcon className="w-3.5 h-3.5 text-olympian-textMuted ml-1" data-id="element-36" />
              </button>

              {sortOpen && !powerDial.active && <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-olympian-border bg-olympian-surface shadow-xl z-50 overflow-hidden animate-crossfade" data-id="element-37">
                  {sortOptions.map(option => <button key={option.value} onClick={() => {
                setSortBy(option.value);
                setSortOpen(false);
              }} className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${sortBy === option.value ? 'bg-olympian-gold/10 text-olympian-gold font-semibold' : 'text-theme hover:bg-white/5'}`} data-id="element-38">
                      {option.label}
                    </button>)}
                </div>}
            </div>
          </div>

          {/* Flat List */}
          <div className="space-y-2 pb-12" data-id="element-39">
            {activeData.map((contact, idx) => {
            const isActive = selectedContact?.id === contact.id;
            // During power dial, disable items that aren't active
            const isDisabled = powerDial.active && !isActive;
            return <ContactCard key={contact.id} contact={contact} isActive={isActive} onClick={handleContactClick} onCall={handleManualCall} disabled={isDisabled} data-id="element-40" />;
          })}
            {activeData.length === 0 && <div className="text-center py-12 text-olympian-textMuted" data-id="element-41">
                No contacts found.
              </div>}
          </div>
        </div>
      </div>
    </div>;
}